import { MusicAPIIntegrationID } from '../music-services/ids';

/**
 * The wire contract of the four Streaming sections for private period numbers (2026-10-16):
 * Audience, Play sources, Short video and Accelerator. Between music24-api and the web and native
 * apps.
 *
 * Spec: docs/superpowers/specs/2026-10-16-music24-streaming-sections-design.md.
 * Design: docs/superpowers/design-reference/streaming-sections/ (frames a to g).
 *
 * Rules of the numbers:
 *   - One source at a time (owner decision O2). Two sources are never added: they count different
 *     people, with different age bands and different names. The answer is for the picked source
 *     (`source`); `sources` lists every connected source for the switch.
 *   - Months, not days (owner decision O5). The sources give period numbers. The period of the
 *     picker gives the months: the calendar months that the period touches and that ended inside
 *     it (`months`). Audience shows one month (unique listeners of two months cannot be added).
 *     Play sources and Accelerator add the months.
 *   - A share is an integer in basis points (`shareBp`, 10000 = 100 %), never a float.
 *   - A number that the source does not give is null, never 0. A share is never turned into a count.
 *   - Free: `locked: 'plan'` and no rows (owner decision O3, the rule of Stores and Countries).
 */

/** The gender values that the sources map to. `unknown`: the source does not know it. */
export type StreamingGender = 'female' | 'male' | 'nonBinary' | 'unknown';

export const STREAMING_GENDERS: readonly StreamingGender[] = ['female', 'male', 'nonBinary', 'unknown'];

/** The short-video platforms (TuneCore `ugc_store`: TikTok 4, Douyin 5, Meta 6, YouTube 8). */
export type StreamingUgcPlatform = 'tiktok' | 'douyin' | 'meta' | 'youtubeShorts';

export const STREAMING_UGC_PLATFORMS: readonly StreamingUgcPlatform[] = ['tiktok', 'douyin', 'meta', 'youtubeShorts'];

/**
 * Who brought a play (design b1): the artist (`you`: the profile, the catalogue), the listeners
 * (their playlists, their library, search), the store (its own playlists and recommendations), or
 * `other`. The tiles add `you` and `listeners` ("Listeners and you").
 */
export type StreamingPlaySourceGroup = 'you' | 'listeners' | 'store' | 'other';

/** Why a connected source cannot be picked in the switch. */
export type StreamingSectionSourceReason =
  /** The source does not report this data (Ditto for Audience). */
  | 'doesNotReport'
  /** The source reports it, but Music24 has no row of it yet (a first read, a new read). */
  | 'notReadYet';

/** One option of the source switch (design a2, b1). */
export interface StreamingSectionSourceOption {
  /**
   * The value of `?source=`: the `integrationType` (`audiomack`), or `<integrationType>:<store>`
   * for one store of a source that gives several (`tunecore:spotify`).
   */
  key: string;
  integrationType: string;
  /** The store of the option (`Spotify`), or null when the source is one store or has no split. */
  store: string | null;
  /** False: the option is dashed and cannot be picked; `reason` says why. */
  gives: boolean;
  reason?: StreamingSectionSourceReason;
  /**
   * The number on the option: the listeners of the shown month (Audience) or the streams of the
   * months (Play sources). Null when the source gives shares only, or has no row.
   */
  number: number | null;
}

/** What every answer of the four routes has. */
export interface StreamingPeriodSectionBase {
  /** False when this deploy has no private-metrics store. */
  available: boolean;
  /** The period of the picker, cut to the plan (the range of the overview). */
  range: { from: string; to: string };
  /** `'plan'`: the plan does not show the section (Free). The answer then has no rows. */
  locked?: 'plan';
  /** The `key` of the picked option of `sources`, or null when no connected source gives the section. */
  source: string | null;
  /** Every connected source, for the switch. Empty when no connected source gives the section. */
  sources: StreamingSectionSourceOption[];
  /** The months of the period (`YYYY-MM`), oldest first. Empty: the period holds no ended month. */
  months: string[];
  /** The months directly before, of the same count, for the change. Empty when none is inside the plan. */
  previousMonths: string[];
  /** The newest ended month that the picked source has numbers for (the a3 button), or null. */
  newestMonth: string | null;
}

/** One gender value of one month. */
export interface StreamingAudienceGender {
  value: StreamingGender;
  /** Null when the source gives the share only (YouTube). */
  listeners: number | null;
  shareBp: number;
}

/** One age band of one month. The band is the source's own (`18-24`, `45+`): never re-cut. */
export interface StreamingAudienceAge {
  band: string;
  listeners: number | null;
  shareBp: number;
}

/** The listeners of one store in one month (TuneCore byStore). */
export interface StreamingAudienceStore {
  store: string;
  listeners: number;
  /** The month before, or null. */
  previousListeners: number | null;
}

/**
 * GET /accounts/:accountId/streaming/audience?days&source&month (design a1-a3, f1, f2)
 *
 * One month of one source. `month` is the month shown: the asked `?month=` (any ended month inside
 * the plan window), else the newest month of `months` with numbers, else null.
 */
export interface StreamingAudienceResponse extends StreamingPeriodSectionBase {
  month: string | null;
  /** The listeners of the month, or null when the source gives shares only. */
  listeners: number | null;
  gender: StreamingAudienceGender[];
  /** In the order of the bands, youngest first. */
  age: StreamingAudienceAge[];
  /** Null when the source has no split by store (owner decision O8). Largest first. */
  stores: StreamingAudienceStore[] | null;
}

/** One source of play over the months. */
export interface StreamingPlaySourceRow {
  /** The source's own name, as given. */
  name: string;
  group: StreamingPlaySourceGroup;
  streams: number;
  /** The same count of months before, or null. */
  previousStreams: number | null;
}

/** The streams of each group in one month (the month bars). */
export interface StreamingPlaySourceMonth {
  month: string;
  you: number;
  listeners: number;
  store: number;
  other: number;
}

/**
 * GET /accounts/:accountId/streaming/playSources?days&source (design b1, b2, f3)
 *
 * The months add up: the streams of a play source are a flow.
 */
export interface StreamingPlaySourcesResponse extends StreamingPeriodSectionBase {
  /** The store of the picked option (`Spotify` for `tunecore:spotify`), or null. */
  store: string | null;
  /** Largest first. */
  rows: StreamingPlaySourceRow[];
  byMonth: StreamingPlaySourceMonth[];
  /**
   * The stores of the picked source that have no split by source (the line under the blocks:
   * "TuneCore reports play sources for Spotify only"). Empty when none.
   */
  storesWithoutSplit: string[];
}

/** The short-video numbers of a scope. A number that the source does not give is null. */
export interface StreamingUgcNumbers {
  creations: number | null;
  views: number | null;
  likes: number | null;
  shares: number | null;
  comments: number | null;
  favourites: number | null;
}

export interface StreamingUgcPlatformRow extends StreamingUgcNumbers {
  platform: StreamingUgcPlatform;
  previousCreations: number | null;
}

/** One day of one platform (only when the source gives days). */
export interface StreamingUgcPoint {
  /** YYYY-MM-DD */
  date: string;
  platform: StreamingUgcPlatform;
  creations: number | null;
  views: number | null;
  likes: number | null;
}

export interface StreamingUgcTrackRow extends StreamingUgcNumbers {
  externalId: string;
  name: string;
  imageUrl: string | null;
  /** The average watch time in milliseconds, or null. */
  avgWatchMs: number | null;
  byPlatform: Array<{ platform: StreamingUgcPlatform; creations: number }>;
}

/**
 * GET /accounts/:accountId/streaming/shortVideo?days&platform&source (design c1, c2, f4)
 *
 * The videos that other people made with the artist's songs. The numbers are flows of the period.
 * A source that gives days fills `series`; one that gives months only answers `series: []` and
 * the months of the period (`months`).
 */
export interface StreamingShortVideoResponse extends StreamingPeriodSectionBase {
  /** The platform filter (`?platform=`), or null for all platforms. */
  platform: StreamingUgcPlatform | null;
  totals: StreamingUgcNumbers;
  /** The period before, or null when it is not inside the plan window. */
  previous: StreamingUgcNumbers | null;
  series: StreamingUgcPoint[];
  /** Every platform the source gives (also with no video in the period). Largest first by videos. */
  platforms: StreamingUgcPlatformRow[];
  /** Largest first by videos. */
  tracks: StreamingUgcTrackRow[];
}

/**
 * One month of Accelerator. A 0 is a real TuneCore number (it fills the months with zeros). A
 * number that TuneCore did not give is null: show a dash, never 0.
 */
export interface StreamingAcceleratorMonth {
  month: string;
  overallStreams: number | null;
  acceleratorStreams: number;
  promotedTracks: number | null;
}

export interface StreamingAcceleratorTrack {
  externalId: string;
  name: string;
  imageUrl: string | null;
  overallStreams: number | null;
  acceleratorStreams: number;
  /** The month before, or null. */
  previousAcceleratorStreams: number | null;
}

/**
 * GET /accounts/:accountId/streaming/accelerator?days (design d1, d2, f5)
 *
 * TuneCore only. A period with no ended month shows the newest month (`byMonth` of `newestMonth`).
 * `allZero`: every month of the last 12 is 0, so Accelerator is probably not on (owner decision O6:
 * the chip stays, with the d2 block).
 */
export interface StreamingAcceleratorResponse extends StreamingPeriodSectionBase {
  byMonth: StreamingAcceleratorMonth[];
  allZero: boolean;
  /** The month of `tracks`: the newest month of `byMonth`. */
  tracksMonth: string | null;
  /** Largest first by Accelerator streams. */
  tracks: StreamingAcceleratorTrack[];
}

/**
 * The fixed groups of the play-source names, by source (`integrationType`) and by the name in
 * lower case. A name that is not here goes through the word rules of `playSourceGroupOf`.
 */
const PLAY_SOURCE_GROUPS: Record<string, Record<string, StreamingPlaySourceGroup>> = {
  // YouTube Analytics `insightTrafficSourceType`, as the provider names them.
  [MusicAPIIntegrationID.YoutubeMusic]: {
    'channel pages': 'you',
    'end screens': 'you',
    'cards and annotations': 'you',
    notifications: 'you',
    'campaign cards': 'you',
    'youtube search': 'listeners',
    playlists: 'listeners',
    'playlist pages': 'listeners',
    external: 'listeners',
    'embedded players': 'listeners',
    'direct or unknown': 'listeners',
    'hashtag pages': 'listeners',
    'suggested videos': 'store',
    'browse features': 'store',
    'shorts feed': 'store',
    'sound pages': 'store',
    remixes: 'store',
    'other youtube pages': 'store',
    'live redirects': 'store',
    advertising: 'other',
    'youtube ads': 'other',
    'product pages': 'other',
  },
};

/** Words that name the group of a name no table has. The first rule that matches wins. */
const GROUP_WORDS: Array<{ group: StreamingPlaySourceGroup; words: readonly string[] }> = [
  { group: 'you', words: ['your ', 'profile', 'artist page', 'catalog', 'catalogue', 'channel', 'own '] },
  {
    group: 'store',
    words: ['editorial', 'algorithmic', 'radio', 'recommend', 'trending', 'browse', 'chart', 'suggest'],
  },
  { group: 'listeners', words: ['library', 'playlist', 'search', 'collection', 'favourite', 'favorite', 'share'] },
];

/**
 * The group of a play-source name. A fixed table first (by source), then word rules for the
 * names of a source with no table yet (TuneCore, Audiomack until the mapping from real rows),
 * then `other`.
 */
export function playSourceGroupOf(integrationType: string, name: string): StreamingPlaySourceGroup {
  const lower = name.trim().toLowerCase();
  const fixed = PLAY_SOURCE_GROUPS[integrationType]?.[lower];
  if (fixed !== undefined) {
    return fixed;
  }
  for (const { group, words } of GROUP_WORDS) {
    if (words.some((word) => lower.includes(word))) {
      return group;
    }
  }
  return 'other';
}

/** The `?source=` key of one store of a source: `tunecore:spotify`. */
export function sectionSourceKey(integrationType: string, store: string | null): string {
  return store === null ? integrationType : `${integrationType}:${store.toLowerCase()}`;
}
